import { useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import type { RadioStation } from '@/mocks/radios';
import { discoveryRows } from '@/mocks/homeDiscovery';

/* ── Card individual ── */
function RadioCard({ station }: { station: RadioStation }) {
  const navigate = useNavigate();

  return (
    <div
      onClick={() => navigate(`/radio/${station.frequency.replace('.', '-')}`)}
      className="group relative flex-shrink-0 w-[150px] sm:w-[170px] md:w-[190px] lg:w-[200px] cursor-pointer snap-start"
    >
      {/* Imagen contenedor */}
      <div className="relative aspect-[3/4] rounded-xl overflow-hidden bg-foreground-900 transition-all duration-300 ease-out group-hover:scale-105 group-hover:shadow-2xl group-hover:z-10"
      >
        {/* Logo / Imagen */}
        <img
          src={station.logoUrl}
          alt={station.name}
          className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-110"
          loading="lazy"
        />

        {/* Overlay gradiente sutil siempre visible */}
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-foreground-950/20 to-transparent" />

        {/* Badge EN VIVO */}
        {station.isLive && (
          <div className="absolute top-2 left-2 flex items-center gap-1 bg-accent-500/90 backdrop-blur-sm px-2 py-0.5 rounded-full"
          >
            <span className="w-1.5 h-1.5 bg-background-50 rounded-full animate-pulse" />
            <span className="text-background-50 text-[9px] font-bold uppercase tracking-wide">EN VIVO</span>
          </div>
        )}

        {/* Badge Premium */}
        {station.isPremium && !station.isLive && (
          <div className="absolute top-2 left-2 flex items-center gap-1 bg-primary-500/90 backdrop-blur-sm px-2 py-0.5 rounded-full"
          >
            <i className="ri-vip-crown-line text-background-50 text-[9px]" />
            <span className="text-background-50 text-[9px] font-bold uppercase tracking-wide">Premium</span>
          </div>
        )}

        {/* Info inferior */}
        <div className="absolute bottom-0 left-0 right-0 p-3"
        >
          {/* Nombre */}
          <h3 className="text-background-50 font-bold text-sm leading-tight truncate group-hover:whitespace-normal group-hover:truncate-none transition-all duration-300"
          >
            {station.name}
          </h3>

          {/* Frecuencia */}
          <p className="text-accent-300 text-xs font-semibold mt-0.5"
          >
            {station.frequency}
          </p>

          {/* Metadata que aparece en hover */}
          <div className="overflow-hidden max-h-0 group-hover:max-h-20 transition-all duration-300 ease-out"
          >
            <div className="pt-2 flex flex-col gap-1"
            >
              {/* Categoría + Ubicación */}
              <div className="flex items-center gap-1"
              >
                <i className="ri-price-tag-3-line text-foreground-400 text-[10px]" />
                <span className="text-foreground-300 text-[10px] truncate">{station.category}</span>
              </div>
              <div className="flex items-center gap-1"
              >
                <i className="ri-map-pin-line text-foreground-400 text-[10px]" />
                <span className="text-foreground-300 text-[10px] truncate">{station.city}</span>
              </div>

              {/* Oyentes + Rating */}
              <div className="flex items-center justify-between mt-1"
              >
                <div className="flex items-center gap-1"
                >
                  <i className="ri-user-line text-foreground-400 text-[10px]" />
                  <span className="text-foreground-300 text-[10px] font-medium">{station.listeners.toLocaleString()}</span>
                </div>
                <div className="flex items-center gap-0.5"
                >
                  <i className="ri-star-fill text-accent-400 text-[9px]" />
                  <span className="text-foreground-300 text-[10px] font-medium">{station.rating}</span>
                </div>
              </div>

              {/* Locutor */}
              <div className="flex items-center gap-1"
              >
                <i className="ri-mic-line text-primary-400 text-[10px]" />
                <span className="text-primary-300 text-[10px] truncate">{station.hostName}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Fila horizontal tipo Netflix ── */
function DiscoveryRow({
  title,
  icon,
  data,
}: {
  title: string;
  icon: string;
  data: RadioStation[];
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(true);
  const [isHovered, setIsHovered] = useState(false);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setShowLeft(el.scrollLeft > 20);
    setShowRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 20);
  }, []);

  const scroll = useCallback((direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const cardWidth = 200 + 16; // ancho card + gap
    const scrollAmount = direction === 'left' ? -cardWidth * 4 : cardWidth * 4;
    el.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    setTimeout(checkScroll, 400);
  }, [checkScroll]);

  return (
    <div
      className="relative py-4"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Título de fila */}
      <div className="flex items-center gap-2 px-4 md:px-8 lg:px-12 mb-3"
      >
        <div className="w-7 h-7 flex items-center justify-center"
        >
          <i className={`${icon} text-accent-500 text-lg`} />
        </div>
        <h2 className="text-foreground-950 font-bold text-lg md:text-xl tracking-tight"
        >
          {title}
        </h2>
        <button
          onClick={() => scrollRef.current?.scrollTo({ left: 0, behavior: 'smooth' })}
          className="ml-auto text-foreground-500 hover:text-accent-500 text-xs font-semibold transition-colors cursor-pointer flex items-center gap-1"
        >
          Ver todo
          <i className="ri-arrow-right-line text-sm" />
        </button>
      </div>

      {/* Contenedor scrollable */}
      <div className="relative"
      >
        {/* Flecha izquierda */}
        {showLeft && (
          <button
            onClick={() => scroll('left')}
            className={`absolute left-0 top-0 bottom-0 z-20 w-12 md:w-16 flex items-center justify-center bg-gradient-to-r from-background-50/90 to-transparent transition-opacity duration-300 cursor-pointer ${isHovered ? 'opacity-100' : 'opacity-0 md:opacity-0'}`}
            aria-label="Scroll izquierda"
          >
            <div className="w-9 h-9 rounded-full bg-background-50/90 backdrop-blur-sm shadow-lg flex items-center justify-center hover:bg-accent-500 hover:text-background-50 transition-all"
            >
              <i className="ri-arrow-left-s-line text-foreground-950 text-xl" />
            </div>
          </button>
        )}

        {/* Flecha derecha */}
        {showRight && (
          <button
            onClick={() => scroll('right')}
            className={`absolute right-0 top-0 bottom-0 z-20 w-12 md:w-16 flex items-center justify-center bg-gradient-to-l from-background-50/90 to-transparent transition-opacity duration-300 cursor-pointer ${isHovered ? 'opacity-100' : 'opacity-0 md:opacity-0'}`}
            aria-label="Scroll derecha"
          >
            <div className="w-9 h-9 rounded-full bg-background-50/90 backdrop-blur-sm shadow-lg flex items-center justify-center hover:bg-accent-500 hover:text-background-50 transition-all"
            >
              <i className="ri-arrow-right-s-line text-foreground-950 text-xl" />
            </div>
          </button>
        )}

        {/* Track de cards */}
        <div
          ref={scrollRef}
          onScroll={checkScroll}
          className="flex gap-4 px-4 md:px-8 lg:px-12 overflow-x-auto scrollbar-hide scroll-smooth snap-x snap-mandatory pb-2"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {data.map((station) => (
            <RadioCard key={`${title}-${station.id}`} station={station} />
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── Sección principal ── */
export default function DiscoverySection() {
  return (
    <section id="descubrir" className="py-8 md:py-12 bg-background-50"
    >
      {/* Header de sección */}
      <div className="px-4 md:px-8 lg:px-12 mb-6"
      >
        <div className="flex items-center gap-3 mb-2"
        >
          <div className="w-10 h-10 rounded-xl bg-accent-500/10 flex items-center justify-center"
          >
            <i className="ri-compass-3-line text-accent-500 text-xl" />
          </div>
          <div>
            <h2 className="text-foreground-950 font-extrabold text-2xl md:text-3xl tracking-tight"
            >
              Descubrir
            </h2>
            <p className="text-foreground-600 text-sm mt-0.5"
            >
              Explorá radios de todo tipo, categorías y ubicaciones
            </p>
          </div>
        </div>
      </div>

      {/* Filas de descubrimiento */}
      {discoveryRows.map((row) => (
        <DiscoveryRow
          key={row.key}
          title={row.title}
          icon={row.icon}
          data={row.data}
        />
      ))}
    </section>
  );
}